import React, { useEffect } from "react"
import { Form, Formik, useFormikContext } from "formik"
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import GeneralInfoSection from "./GeneralInfoSection"
import NotesSection from "./NotesSection"
import { enterpriseInitialValues, enterpriseSchema } from "./enterpriseSchema"

const sx = {
  dialog: {
    "& .MuiDialog-paper": {
      width: 560,
      maxWidth: "calc(100vw - 32px)",
      borderRadius: "12px",
      boxShadow: "0 12px 40px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06)",
      overflow: "hidden",
    },
  },
  title: {
    px: 4.6,
    pt: 4,
    pb: 1,
    fontSize: 20,
    fontWeight: 400,
    lineHeight: 1.3,
    color: "text.primary",
    pr: 7,
  },
  closeBtn: {
    position: "absolute",
    right: 16,
    top: 16,
    color: "text.secondary",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: 3,
    px: 4.6,
    pt: 3,
    pb: 1,
    overflowY: "auto",
    flex: "1 1 auto",
    minHeight: 0,
  },
  serverError: {
    mx: 5,
    mt: 2,
    fontSize: 14,
    border: "1px solid",
    borderColor: "error.main",
  },
  actions: {
    px: 4.6,
    pt: 3,
    pb: 4,
    justifyContent: "space-between",
  },
  cancelBtn: {
    fontSize: 14,
    fontWeight: 500,
    letterSpacing: "0.04em",
    px: 2,
    py: "10px",
  },
  submitBtn: {
    fontSize: 14,
    fontWeight: 500,
    letterSpacing: "0.04em",
    px: "28px",
    py: "12px",
    borderRadius: 1,
    boxShadow: "0 2px 4px rgba(33, 150, 243, 0.3)",
  },
}

const SyncServerErrors = ({ serverErrors }) => {
  const { setFieldError } = useFormikContext()

  useEffect(() => {
    if (!serverErrors) return
    Object.entries(serverErrors).forEach(([field, message]) => setFieldError(field, message))
  }, [serverErrors])

  return null
}

const EnterpriseFormModal = ({ open, onClose, onSubmit, initialValues, serverErrors }) => {
  const isEdit = !!initialValues

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      await onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} sx={sx.dialog}>
      <Formik
        initialValues={initialValues ?? enterpriseInitialValues}
        validationSchema={enterpriseSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting, dirty }) => (
          <Form style={{ display: "flex", flexDirection: "column", flex: "1 1 auto", overflow: "hidden" }}>
            <DialogTitle sx={sx.title}>
              {isEdit ? "Modifier l'entreprise" : "Créer une nouvelle entreprise"}
              <IconButton onClick={onClose} disabled={isSubmitting} sx={sx.closeBtn}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>

            <SyncServerErrors serverErrors={serverErrors} />
            <DialogContent sx={sx.content}>
              <GeneralInfoSection />
              <NotesSection />
            </DialogContent>

            <DialogActions sx={sx.actions}>
              <Button variant="text" onClick={onClose} disabled={isSubmitting} sx={sx.cancelBtn}>
                Annuler
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting || (!dirty && isEdit)}
                startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                sx={sx.submitBtn}
              >
                {isSubmitting ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

export default EnterpriseFormModal
